import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import StorageService from '../services/storageService';

interface RequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

const axiosBase = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL_PATH,
  timeout: 60000, // 60000 milliseconds = 60 seconds
});

// Request interceptor
axiosBase.interceptors.request.use(
  (config) => {
    const token = StorageService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for automatic token refresh
axiosBase.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If the error is 401 and we haven't already tried to refresh
    // Skip token refresh for signup endpoints as they don't require authentication
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/signup/')) {
      originalRequest._retry = true;

      try {
        // Try to refresh the token
        const refreshToken = StorageService.getRefreshToken();
        if (refreshToken) {
          const response = await axios.post(
            `${process.env.REACT_APP_BASE_URL_PATH}/api/v1/auth/refresh`,
            { refreshToken },
            { headers: { Authorization: `Bearer ${refreshToken}` } }
          );

          if (response.data?.accessToken) {
            // Save the new tokens
            StorageService.saveTokens(response.data);
            
            // Retry the original request with the new token
            originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;
            return axiosBase(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        // If refresh fails, clear tokens and let the error propagate
        StorageService.clearTokens();
      }
    }

    return Promise.reject(error);
  }
);

const doRequest = async ({ method, url, payload, config, completeResponse = true }: {
  method: 'get' | 'post' | 'put' | 'delete' | 'patch';
  url: string;
  payload?: any;
  config?: RequestConfig;
  completeResponse?: boolean;
}) => {
  try {
    let result;
    switch (method) {
      case 'get':
        result = await axiosBase.get(url, config);
        break;
      case 'delete':
        result = await axiosBase.delete(url, { ...config, data: payload });
        break;
      case 'post':
        result = await axiosBase.post(url, payload, config);
        break;
      case 'put':
        result = await axiosBase.put(url, payload, config);
        break;
      case 'patch':
        result = await axiosBase.patch(url, payload, config);
        break;
      default:
        throw new Error(`Unsupported method: ${method}`);
    }
    return completeResponse ? result : result.data
  } catch (error) {
    throw error;
  }
};

export default doRequest; 